import { useMemo } from "react";
import { ProductCard } from "./ProductCard";
import { scrollToId } from "./Navbar";

const groupProducts = (products) => {
  const groups = [];
  const map = new Map();
  for (const p of products) {
    const g = p.group || "";
    if (!map.has(g)) {
      map.set(g, []);
      groups.push(g);
    }
    map.get(g).push(p);
  }
  return groups.map((g) => ({ group: g, products: map.get(g) }));
};

export const Catalogue = ({ categories, activeCat, setActiveCat, loading, query, setQuery }) => {

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let cats = activeCat === "all" ? categories : categories.filter((c) => c.slug === activeCat);
    if (!q) return cats;
    return cats
      .map((c) => {
        const catMatch = c.name.toLowerCase().includes(q);
        return {
          ...c,
          products: catMatch ? c.products : c.products.filter((p) => p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)),
        };
      })
      .filter((c) => c.products.length > 0);
  }, [categories, activeCat, query]);

  const searchResults = useMemo(
    () => (query.trim() ? visible.flatMap((c) => c.products) : []),
    [visible, query]
  );
  const searching = query.trim().length > 0;

  return (
    <section id="catalogue" data-testid="catalogue-section" className="max-w-[1500px] mx-auto px-5 sm:px-6 lg:px-12 pt-3 sm:pt-4 pb-8 sm:pb-10">
      <div
        className="bg-white border border-[#221533]/10 rounded-2xl shadow-[0_15px_40px_-20px_rgba(142,90,158,0.35)] p-2 sm:p-3 mb-3"
        data-testid="category-filters"
      >
        <div className="flex flex-nowrap sm:flex-wrap gap-1.5 sm:gap-2 overflow-x-auto sm:overflow-visible no-scrollbar -mx-0.5 px-0.5">
          {[{ slug: "all", name: "All", products: categories.flatMap((c) => c.products) }, ...categories].map((c) => (
            <button
              key={c.slug}
              data-testid={`filter-${c.slug}`}
              onClick={() => {
                setActiveCat(c.slug);
                scrollToId("#catalogue");
              }}
              className={`group shrink-0 sm:shrink sm:flex-1 sm:min-w-[110px] inline-flex flex-row items-center justify-center gap-1.5 sm:gap-2 font-manrope font-semibold text-xs sm:text-sm tracking-wide uppercase px-4 sm:px-3 py-2.5 sm:py-3.5 rounded-xl border text-center leading-tight whitespace-nowrap transition-[background-color,color,border-color,box-shadow] duration-300 active:scale-95 ${
                activeCat === c.slug
                  ? "bg-[#8E5A9E] border-[#8E5A9E] text-white font-bold shadow-[0_12px_28px_-8px_rgba(142,90,158,0.6)]"
                  : "border-transparent text-[#221533]/75 hover:bg-[#8E5A9E] hover:border-[#8E5A9E] hover:text-white hover:shadow-[0_10px_22px_-8px_rgba(142,90,158,0.55)]"
              }`}
            >
              {c.name}
              <span
                className={`text-[9px] px-2 py-0.5 rounded-full transition-colors duration-300 ${
                  activeCat === c.slug ? "bg-white/25 text-white" : "bg-[#8E5A9E]/10 text-[#74427F] group-hover:bg-white/25 group-hover:text-white"
                }`}
              >
                {c.products.length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 sm:gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[4/6] bg-[#221533]/[0.04] border border-[#221533]/10 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : searching ? (
        <div data-testid="search-results">
          <p className="font-manrope font-semibold text-sm text-[#221533]/70 mb-3">
            {searchResults.length} result{searchResults.length === 1 ? "" : "s"} for “{query.trim()}”
          </p>
          {searchResults.length === 0 ? (
            <p data-testid="no-results" className="text-[#221533]/50 font-light py-16 text-center">
              No products match “{query}” — try another search.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 sm:gap-6">
              {searchResults.map((p, i) => (
                <ProductCard key={`${p.slug}-${i}`} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      ) : (
        visible.map((cat) => {
          const grouped = groupProducts(cat.products);
          const hasGroups = grouped.length > 1 || (grouped.length === 1 && grouped[0].group !== "");
          return (
            <div key={cat.slug} className="mb-16 sm:mb-20 last:mb-0">
              <div className="flex items-baseline justify-between border-b border-[#221533]/10 pb-2 mb-3">
                <h3 className="font-clash text-2xl lg:text-3xl font-medium tracking-tight text-[#221533]">{cat.name}</h3>
                <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#221533]/40">
                  {cat.products.length} SKUs
                </span>
              </div>
              {grouped.map((g) => (
                <div key={g.group || "all"} className="mb-6 last:mb-0">
                  {hasGroups && g.group && (
                    <p data-testid={`subcat-${cat.slug}-${g.group.toLowerCase().replace(/ /g, "-")}`} className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#8E5A9E] mb-3 flex items-center gap-3">
                      <span className="w-6 h-px bg-[#8E5A9E]/50" /> {g.group}
                    </p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 sm:gap-6">
                    {g.products.map((p, i) => (
                      <ProductCard key={`${p.slug}-${i}`} product={p} index={i} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })
      )}
    </section>
  );
};
